import { FolderOpenIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";

import { useDragDrop } from "../DragAndDrop/DragDropProvider";

import useExpandedItems from "./hooks/useExpandedItems";
import ListItem from "./ListItem";
import TreeLines from "./TreeLines";
import { ExpandedContentProps } from "./types";
import { TREE_CONSTANTS } from "./utils";

import { EntityType, getEntityNumericId } from "@/queries/folder-entities/types";
import LoadingSpinner from "@/components/LoadingSpinner";

const ExpandedContent: React.FC<ExpandedContentProps> = observer(
  ({
    item,
    isExpanded,
    depth,

    isCompact = false,
    onItemClick,
  }) => {
    const expandItemsQuery = useExpandedItems(isExpanded ? getEntityNumericId(item.id) : undefined);
    const { activeItem } = useDragDrop();

    // Check if this parent folder is being dragged
    const isParentBeingDragged =
      activeItem?.id === item.id && activeItem?.type === EntityType.FOLDER;

    if (!isExpanded) {
      return null;
    }

    if (expandItemsQuery.isLoading) {
      return (
        <div className="relative">
          <div
            className="flex items-center justify-center border-b border-gray-100 bg-gray-50 py-4"
            style={{
              paddingLeft: `${32 + (depth + 1) * TREE_CONSTANTS.INDENT_WIDTH}px`,
              paddingRight: "16px",
            }}
          >
            <LoadingSpinner size="4" />
          </div>
        </div>
      );
    }

    if (!expandItemsQuery.data?.entities || expandItemsQuery.data.entities.length === 0) {
      return (
        <div className="relative">
          <TreeLines depth={depth + 1} />
          <div
            className="flex items-center border-b border-gray-100 py-4"
            style={{
              paddingLeft: `${32 + (depth + 1) * TREE_CONSTANTS.INDENT_WIDTH}px`,
              paddingRight: "16px",
            }}
          >
            <FolderOpenIcon className="mr-2 h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500">This folder is empty</span>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        {expandItemsQuery.data.entities.map((childItem, index) => {
          const isLastChildItem = index === expandItemsQuery.data.entities.length - 1;
          return (
            <ListItem
              key={childItem.id}
              item={childItem}
              isCompact={isCompact}
              depth={depth + 1}
              isLastChild={isLastChildItem}
              droppableDisabled={isParentBeingDragged}
              onItemClick={onItemClick}
            />
          );
        })}
      </div>
    );
  },
);

export default ExpandedContent;
